import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { EducationLevelRadioGroup } from '../EducationLevelRadioGroup';
import { useSubjects } from '@/hooks/useSubjects';
import { COURSE_TYPES, CLASS_TYPES, CLASS_LANGUAGES } from '@/lib/education-levels';
import { CreateTemplateFormData } from './types';
import { Loader2 } from 'lucide-react';

interface StepClassInfoProps {
  formData: CreateTemplateFormData;
  updateFormData: (updates: Partial<CreateTemplateFormData>) => void;
}

export function StepClassInfo({ formData, updateFormData }: StepClassInfoProps) {
  const { data: subjects, isLoading: subjectsLoading } = useSubjects();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">รายวิชา</CardTitle>
        </CardHeader>
        <CardContent>
          {subjectsLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              กำลังโหลดวิชา...
            </div>
          ) : (
            <RadioGroup
              value={formData.subject}
              onValueChange={(v) => updateFormData({ subject: v })}
              className="flex flex-wrap gap-3"
            >
              {subjects?.map((subj) => (
                <div key={subj.name} className="flex items-center space-x-2">
                  <RadioGroupItem value={subj.name} id={`subject-${subj.name}`} />
                  <Label htmlFor={`subject-${subj.name}`} className="cursor-pointer">
                    {subj.name}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ระดับชั้น</CardTitle>
        </CardHeader>
        <CardContent>
          <EducationLevelRadioGroup
            value={formData.education_level}
            onChange={(v) => updateFormData({ education_level: v })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ประเภทคอร์ส</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={formData.course_type}
            onValueChange={(v) => updateFormData({ course_type: v as 'REGULAR' | 'INTER' })}
            className="flex gap-6"
          >
            {COURSE_TYPES.map((type) => (
              <div key={type.value} className="flex items-center space-x-2">
                <RadioGroupItem value={type.value} id={`course-${type.value}`} />
                <Label htmlFor={`course-${type.value}`} className="cursor-pointer">
                  {type.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ประเภทคลาส</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={formData.class_type}
            onValueChange={(v) => updateFormData({ class_type: v as 'PREMIER' | 'PRIVATE' })}
            className="flex gap-6"
          >
            {CLASS_TYPES.map((type) => (
              <div key={type.value} className="flex items-center space-x-2">
                <RadioGroupItem value={type.value} id={`class-${type.value}`} />
                <Label htmlFor={`class-${type.value}`} className="cursor-pointer">
                  {type.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ภาษา</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={formData.language}
            onValueChange={(v) => updateFormData({ language: v as 'THAI' | 'ENGLISH' })}
            className="flex gap-6"
          >
            {CLASS_LANGUAGES.map((lang) => (
              <div key={lang.value} className="flex items-center space-x-2">
                <RadioGroupItem value={lang.value} id={`lang-${lang.value}`} />
                <Label htmlFor={`lang-${lang.value}`} className="cursor-pointer">
                  {lang.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>
    </div>
  );
}
